import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FolderOpen, Upload, FilePdf, FileDoc, FileXls, FileVideo,
  FilePpt, FileImage, FileZip, DownloadSimple, Trash, Pencil, MagnifyingGlass,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  brochure: 'Brochure', listino: 'Listini', presentazione: 'Presentazioni',
  informative: 'Informative', modulistica: 'Modulistica', video: 'Video', altro: 'Altro',
};

const CATEGORY_COLORS = {
  brochure: 'bg-orange-100 text-orange-700',
  listino: 'bg-blue-100 text-blue-700',
  presentazione: 'bg-purple-100 text-purple-700',
  informative: 'bg-cyan-100 text-cyan-700',
  modulistica: 'bg-amber-100 text-amber-700',
  video: 'bg-red-100 text-red-700',
  altro: 'bg-slate-100 text-slate-600',
};

function fileIcon(filename = '') {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return FilePdf;
  if (['doc', 'docx'].includes(ext)) return FileDoc;
  if (['xls', 'xlsx'].includes(ext)) return FileXls;
  if (['ppt', 'pptx'].includes(ext)) return FilePpt;
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return FileImage;
  if (['mp4', 'mov'].includes(ext)) return FileVideo;
  if (['zip'].includes(ext)) return FileZip;
  return FilePdf;
}

function fmtSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export default function SalesKitPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: 'altro' });
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'altro', file: null });
  const [showUpload, setShowUpload] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set('category', filter);
      const res = await fetch(`${API}/api/sales-kit/items?${params}`, { credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const upload = async () => {
    if (!uploadForm.file) return toast.error('Seleziona un file');
    if (!uploadForm.title) return toast.error('Titolo obbligatorio');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadForm.file);
      fd.append('title', uploadForm.title);
      fd.append('description', uploadForm.description);
      fd.append('category', uploadForm.category);
      const res = await fetch(`${API}/api/sales-kit/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Documento caricato');
        setShowUpload(false);
        setUploadForm({ title: '', description: '', category: 'altro', file: null });
        fetchData();
      } else toast.error(data.detail || 'Errore upload');
    } catch (e) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const download = async (it) => {
    window.open(`${API}/api/sales-kit/${it.id}/download`, '_blank');
    toast.success(`Download: ${it.title}`);
    setTimeout(fetchData, 1000);
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare il documento?')) return;
    try {
      const res = await fetch(`${API}/api/sales-kit/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Eliminato'); fetchData(); }
    } catch (e) { toast.error('Errore'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const res = await fetch(`${API}/api/sales-kit/${editing.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { toast.success('Aggiornato'); setEditing(null); fetchData(); }
    } catch (e) { toast.error('Errore'); }
  };

  const filtered = items.filter(it =>
    !search || it.title?.toLowerCase().includes(search.toLowerCase()) ||
    it.description?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {};
  items.forEach(it => { stats[it.category] = (stats[it.category] || 0) + 1; });

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="sales-kit-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FolderOpen size={32} weight="duotone" className="text-orange-500" />
            Folder
          </h1>
          <p className="text-slate-500 mt-1">Documentazione commerciale: brochure, listini, presentazioni, modulistica.</p>
        </div>
        <Button onClick={() => setShowUpload(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="upload-kit-btn">
          <Upload size={18} className="mr-2" /> Carica documento
        </Button>
      </div>

      {/* CATEGORY TILES */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        <div onClick={() => setFilter('')}
             className={`bg-white rounded-lg border p-3 cursor-pointer transition ${!filter ? 'ring-2 ring-orange-500' : 'hover:bg-slate-50'}`}>
          <div className="text-xs text-slate-500">Tutti</div>
          <div className="text-xl font-bold">{items.length}</div>
        </div>
        {Object.keys(CATEGORY_LABELS).map(cat => (
          <div key={cat} onClick={() => setFilter(filter === cat ? '' : cat)}
               className={`bg-white rounded-lg border p-3 cursor-pointer transition ${filter === cat ? 'ring-2 ring-orange-500' : 'hover:bg-slate-50'}`}
               data-testid={`category-${cat}`}>
            <div className="text-xs text-slate-500">{CATEGORY_LABELS[cat]}</div>
            <div className="text-xl font-bold">{stats[cat] || 0}</div>
          </div>
        ))}
      </div>

      {/* SEARCH */}
      <div className="bg-white border rounded-lg p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-slate-400 ml-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Cerca per titolo/descrizione..." className="border-0 focus-visible:ring-0" />
      </div>

      {/* GRID */}
      {filtered.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <FolderOpen size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">Nessun documento. Inizia caricando il primo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(it => {
            const Icon = fileIcon(it.filename);
            return (
              <div key={it.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition" data-testid={`kit-item-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <Icon size={24} className="text-orange-500" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{it.title}</h3>
                      <Badge className={`${CATEGORY_COLORS[it.category]} text-xs mt-1`}>{CATEGORY_LABELS[it.category]}</Badge>
                      {it.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{it.description}</p>}
                      <div className="text-xs text-slate-400 mt-2">
                        {fmtSize(it.size)} · {it.downloads || 0} download
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button size="sm" onClick={() => download(it)} className="flex-1 bg-orange-500 hover:bg-orange-600"
                          data-testid={`download-${it.id}`}>
                    <DownloadSimple size={14} className="mr-1" /> Scarica
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditing(it);
                    setEditForm({ title: it.title, description: it.description || '', category: it.category });
                  }}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="text-red-600">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* UPLOAD DIALOG */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica nuovo documento</DialogTitle>
            <DialogDescription>Aggiungi al Folder documenti accessibili a tutti i consulenti.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>File *</Label>
              <input type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg,.webp,.mp4,.mov,.zip"
                     onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                     className="text-sm w-full" data-testid="upload-file-input" />
            </div>
            <div>
              <Label>Titolo *</Label>
              <Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                     placeholder="Es: Brochure Luce Domestica 2026" data-testid="upload-title" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea rows={3} value={uploadForm.description}
                        onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
            <Button onClick={upload} disabled={uploading} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-upload-btn">
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica documento</DialogTitle>
            <DialogDescription>Aggiorna titolo, categoria o descrizione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div><Label>Categoria</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label>Descrizione</Label>
              <Textarea rows={3} value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={saveEdit} className="bg-orange-500 hover:bg-orange-600">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
